import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogo, ArrowRight, Eye, EyeSlash, HourglassMedium } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguagePicker } from "../components/LanguagePicker";

const BG = "https://images.unsplash.com/photo-1498262257252-c282316270bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [notice, setNotice] = useState(sp.get("notice") || "");
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        setSent(true);
        toast.success(data.message || "Reset link sent if the account exists");
        return;
      }
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { email, password, name, company_name: companyName };
      const { data } = await api.post(endpoint, payload);
      if (data.pending) { setNotice(data.message); setMode("login"); return; }
      setUser(data);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (err.response?.status === 403) setNotice(msg); else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background relative">
      <div className="absolute top-3 right-3 z-20"><LanguagePicker persist={false} /></div>
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] text-white overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 flex items-center gap-2">
          <img src="/nucleus-logo.png" alt="" className="h-10 w-10 object-contain" />
          <span className="font-bold text-lg tracking-tight">Neuclius</span>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="overline-label text-white/60 mb-4">Cloud Business Management</p>
          <h1 className="text-4xl font-black tracking-tight leading-tight">
            Run finance, sales, purchasing & inventory from one command center.
          </h1>
          <p className="text-sm text-white/60 mt-6 leading-relaxed">
            A Business Central-class ERP with a built-in AI controller that reads your live
            ledgers and answers in plain language.
          </p>
        </div>
        <div className="relative z-10 flex gap-8 text-xs text-white/40 font-mono">
          <span>GENERAL LEDGER</span>
          <span>AR / AP</span>
          <span>INVENTORY</span>
          <span>AI INSIGHTS</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/nucleus-logo.png" alt="" className="h-10 w-10 object-contain" />
            <span className="font-bold text-lg tracking-tight">Neuclius</span>
          </div>

          <p className="overline-label">{mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Password reset"}</p>
          <h2 className="text-2xl font-black tracking-tight mt-1 mb-6">
            {mode === "login" ? "Access your workspace" : mode === "register" ? "Create your company" : "Forgot your password?"}
          </h2>

          {notice && (
            <div className="mb-5 flex gap-2.5 border border-amber-300 bg-amber-50 text-amber-900 p-3 rounded-sm text-sm" data-testid="approval-notice">
              <HourglassMedium size={18} className="shrink-0 mt-0.5" />
              <span>{notice}</span>
            </div>
          )}

          {mode === "forgot" ? (
            sent ? (
              <div data-testid="forgot-sent-message" className="space-y-5">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  If an account exists for <strong className="text-foreground">{email}</strong>, we've emailed a link to reset your password. The link expires in 1 hour.
                </p>
                <p className="text-sm text-muted-foreground">Didn't get it? Check spam, or try again in a moment.</p>
                <Button
                  onClick={() => { setMode("login"); setSent(false); }}
                  data-testid="back-to-login-button"
                  className="w-full rounded-sm bg-primary hover:bg-primary/90 h-10 font-semibold"
                >
                  Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed -mt-2 mb-2">
                  Enter the email on your account and we'll send you a link to set a new password.
                </p>
                <div>
                  <Label className="overline-label" htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email" type="email" required data-testid="forgot-email-input"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 rounded-sm" placeholder="you@company.com"
                  />
                </div>
                <Button
                  type="submit" disabled={loading} data-testid="forgot-submit-button"
                  className="w-full rounded-sm bg-primary hover:bg-primary/90 h-10 font-semibold group"
                >
                  {loading ? "Sending…" : "Send reset link"}
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <button
                  type="button" data-testid="forgot-back-link"
                  onClick={() => setMode("login")}
                  className="w-full text-sm text-primary font-semibold hover:underline"
                >
                  Back to sign in
                </button>
              </form>
            )
          ) : (
          <>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="overline-label" htmlFor="name">Full name</Label>
                <Input
                  id="name" data-testid="register-name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1 rounded-sm" placeholder="Jane Controller"
                />
              </div>
            )}
            {mode === "register" && (
              <div>
                <Label className="overline-label" htmlFor="company">Company name</Label>
                <Input
                  id="company" data-testid="register-company-input"
                  value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                  className="mt-1 rounded-sm" placeholder="Acme Pty Ltd"
                />
                <p className="text-xs text-muted-foreground mt-1">You get your own workspace with a few sample records to start from.</p>
              </div>
            )}
            <div>
              <Label className="overline-label" htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-sm" placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="overline-label" htmlFor="password">Password</Label>
                {mode === "login" && (
                  <button
                    type="button" data-testid="forgot-password-link"
                    onClick={() => { setMode("forgot"); setSent(false); }}
                    className="text-xs font-semibold text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative mt-1">
                <Input
                  id="password" type={showPw ? "text" : "password"} required data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm pr-10" placeholder="••••••••"
                />
                <button
                  type="button" data-testid="toggle-password-visibility"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  title={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full rounded-sm bg-primary hover:bg-primary/90 h-10 font-semibold group"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="overline-label">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button
            variant="outline" onClick={googleLogin} data-testid="google-login-button"
            className="w-full rounded-sm h-10 font-medium border-border hover:bg-secondary"
          >
            <GoogleLogo size={18} weight="bold" className="mr-2" />
            Continue with Google
          </Button>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "No account yet? " : "Already have an account? "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-semibold hover:underline"
            >
              {mode === "login" ? "Register" : "Sign in"}
            </button>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
